import { Col, DeleteButton, EditButton, Form, getDefaultSortOrder, Icons, Input, List, Row, Select, Space, Table, TagField, TextField, useTable } from '@pankod/refine-antd';
import { CrudFilters, IResourceComponentsProps, useSelect } from '@pankod/refine-core';
import type { CheckboxValueType } from 'antd/es/checkbox/Group';
import type { ColumnsType } from 'antd/es/table';
import { ButtonColumn } from 'components/buttonColumn';
import { ButtonExport } from 'components/buttonExport';
import { ButtonFilter } from 'components/buttonFilter';
import useMediaScreen from 'hooks/useMediaScreen';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { IGenre } from 'types/genre.interface';
import { IRelease, IReleaseResponse } from 'types/releaase.interface';
import { ITag } from 'types/tag.interface';
import { ITrack } from 'types/track.interface';
import { IUser } from 'types/user.interface';
import { formatDateTime, getOrderNumber, includesClassName } from 'utils';

export const ReleaseList: React.FC<IResourceComponentsProps> = () => {
    const { isMobile } = useMediaScreen();
    const { options: labels } = useSelect<any>({
        resource: 'labels',
        fetchSize: 100,
        optionLabel: 'name',
        optionValue: '_id',
    });
    const { tableProps, sorter, searchFormProps }: any = useTable<IReleaseResponse>({
        resource: 'multipacks',
        initialPageSize: 100,
        onSearch: (params) => {
            const filters: CrudFilters = [];
            const input: any = params;
            filters.push({
                field: 'search',
                operator: 'in',
                value: { search: input.search || '', labelId: input.labelId || '', contributors: input?.contributors || [] },
            });

            return filters;
        },
    });
    const { options: contributors } = useSelect<IUser>({
        resource: 'users/contributor',
        optionLabel: 'username',
        optionValue: '_id',
    });

    const current = tableProps?.pagination?.current;
    const pageSize = tableProps?.pagination?.pageSize;

    const defaultColumn: CheckboxValueType[] = ['title', 'user', 'status', 'totalBuys', 'createdAt'];

    const [hideCols, setHideCols] = useState<CheckboxValueType[]>(defaultColumn);

    const options = [
        { label: 'Title', value: 'title' },
        { label: 'Description', value: 'description' },
        { label: 'Owner', value: 'user' },
        { label: 'Status', value: 'status' },
        { label: 'Label', value: 'label' },
        { label: 'Genres', value: 'genres' },
        { label: 'Tags', value: 'tags' },
        { label: 'Tracks', value: 'tracks' },
        { label: 'CreatedAt', value: 'createdAt' },
        { label: 'All Time Buys', value: 'totalBuys' },
        { label: 'Enabled', value: 'isEnabled' },
        { label: 'Featured', value: 'isFeatured' },
    ];

    const columns: ColumnsType<IRelease> = [
        {
            title: 'Index',
            dataIndex: '_id',
            key: '_id',
            render: (value, item, index) => <TextField value={getOrderNumber(current, pageSize, index)} />,
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (value) => <TextField value={value} />,
            className: includesClassName(hideCols, 'title'),
            defaultSortOrder: getDefaultSortOrder('title', sorter),
            sorter,
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (value) => <TextField value={value} />,
            className: includesClassName(hideCols, 'description'),
            defaultSortOrder: getDefaultSortOrder('description', sorter),
            sorter,
        },
        {
            title: 'Owner',
            dataIndex: 'user',
            key: 'user',
            render: (value) => <TextField value={value?.username} />,
            className: includesClassName(hideCols, 'user'),
            defaultSortOrder: getDefaultSortOrder('user', sorter),
            sorter,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (value) => <TextField value={value} />,
            className: includesClassName(hideCols, 'status'),
            defaultSortOrder: getDefaultSortOrder('status', sorter),
            sorter,
        },
        {
            title: 'Label',
            dataIndex: 'label',
            key: 'label',
            render: (value) => <TextField value={value?.name} />,
            className: includesClassName(hideCols, 'label'),
            defaultSortOrder: getDefaultSortOrder('label', sorter),
            sorter,
        },
        {
            title: 'Genres',
            dataIndex: 'genres',
            key: 'genres',
            render: (value) => (
                <div>
                    {value.map((item: IGenre) => (
                        <TagField value={item?.name} />
                    ))}
                </div>
            ),
            className: includesClassName(hideCols, 'genres'),
            defaultSortOrder: getDefaultSortOrder('genres', sorter),
            sorter,
        },
        {
            title: 'tags',
            dataIndex: 'tags',
            key: 'tags',
            render: (value) => (
                <div>
                    {value.map((item: ITag) => (
                        <TagField value={item?.name} />
                    ))}
                </div>
            ),
            className: includesClassName(hideCols, 'tags'),
            defaultSortOrder: getDefaultSortOrder('tags', sorter),
            sorter,
        },
        {
            title: 'Tracks',
            dataIndex: 'tracks',
            key: 'tracks',
            render: (value) => (
                <div>
                    {value.map((item: ITrack) => (
                        <TextField value={`${item?.title}, `} />
                    ))}
                </div>
            ),
            className: includesClassName(hideCols, 'tracks'),
            defaultSortOrder: getDefaultSortOrder('tracks', sorter),
            sorter,
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (value) => <TextField value={formatDateTime(value)} />,
            className: includesClassName(hideCols, 'createdAt'),
            defaultSortOrder: getDefaultSortOrder('createdAt', sorter),
            sorter,
        },
        {
            title: 'All Time Buys',
            dataIndex: 'totalBuys',
            key: 'totalBuys',
            render: (value, item) => {
                return (
                    <Link to={`/multipacks/show/${item._id}`}>
                        <TextField style={{ color: 'blue' }} value={value} />
                    </Link>
                );
            },

            className: includesClassName(hideCols, 'totalBuys'),
            defaultSortOrder: getDefaultSortOrder('totalBuys', sorter),
            sorter,
        },
        {
            title: 'Enabled',
            dataIndex: 'isEnabled',
            key: 'isEnabled',
            render: (value) => <TextField value={value ? 'Yes' : 'No'} />,
            className: includesClassName(hideCols, 'isEnabled'),
            defaultSortOrder: getDefaultSortOrder('isEnabled', sorter),
            sorter,
        },
        {
            title: 'Featured',
            dataIndex: 'isFeatured',
            key: 'isFeatured',
            render: (value) => <TextField value={value ? 'Yes' : 'No'} />,
            className: includesClassName(hideCols, 'isFeatured'),
            defaultSortOrder: getDefaultSortOrder('isFeatured', sorter),
            sorter,
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <EditButton hideText size='small' recordItemId={record._id} />
                    <DeleteButton hideText size='small' recordItemId={record._id} />
                </Space>
            ),
        },
    ];

    return (
        <List>
            <Row style={{ display: 'block' }}>
                <div style={{ display: 'flex', gap: '5px', marginBottom: '5px' }}>
                    <TextField value='Total:' style={{ color: '#626262', fontSize: '14px', fontWeight: 700 }} />
                    <TextField value={tableProps?.pagination?.total} style={{ color: '#626262', fontSize: '14px', fontWeight: 700 }} />
                </div>
                <Col>
                    <Form layout='vertical' {...searchFormProps}>
                        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
                            <Form.Item label='Search' name='search' style={{ width: isMobile ? '100%' : '40%', marginRight: '15px' }} initialValue=''>
                                <Input placeholder='Title, Status' prefix={<Icons.SearchOutlined />} />
                            </Form.Item>
                            <Form.Item style={{ width: isMobile ? '100%' : '30%' }} label='Label' name='labelId'>
                                <Select defaultValue={{ value: '', label: 'All label' }} options={labels} />
                            </Form.Item>
                            <Form.Item label='Contributors' style={{ width: isMobile ? '100%' : '30%', marginLeft: isMobile ? '0' : '15%' }} name='contributors'>
                                <Select
                                    placeholder='Select contributors'
                                    // mode='multiple'
                                    options={contributors}
                                />
                            </Form.Item>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Form.Item>
                                <ButtonFilter />
                            </Form.Item>
                            <div>
                                <ButtonColumn options={options} setHideCols={setHideCols} defaultColumn={defaultColumn} />
                                <ButtonExport path='multipacks' />
                            </div>
                        </div>
                    </Form>
                </Col>
                <Col>
                    <Table
                        {...tableProps}
                        pagination={{
                            ...tableProps?.pagination,
                            showSizeChanger: true,
                        }}
                        columns={columns}
                        rowKey='id'
                    />
                </Col>
            </Row>
        </List>
    );
};
